<template>
	<div class="nav-wrapper">
		<button class="hamburger-btn" @click="toggleNav" :aria-label="isExpanded ? 'Close menu' : 'Open menu'">
			<span class="hamburger-icon" :class="{ active: isExpanded }">
				<span></span>
				<span></span>
				<span></span>
			</span>
		</button>

		<nav class="admin-side-nav" :class="{ expanded: isExpanded }">
			<div class="nav-header">
				<div class="header-title">
					<h2>Admin</h2>
					<button class="dark-mode-toggle" @click="toggleDarkMode" :title="isDarkMode ? 'Light Mode' : 'Dark Mode'" aria-label="Toggle dark mode">
						{{ isDarkMode ? '☀️' : '🌙' }}
					</button>
				</div>
				<button class="close-btn" @click="toggleNav" aria-label="Close menu">✕</button>
			</div>

			<div class="nav-menu">
				<template v-for="item in menuItems" :key="item.id">
					<div
						v-if="!item.children"
						class="nav-item"
						:class="{ active: currentActiveItem === item.id }"
						@click="selectItem(item.id)"
					>
						<span class="nav-icon">{{ item.icon }}</span>
						<span class="nav-label">{{ item.label }}</span>
					</div>

					<div v-else class="nav-parent">
						<div
							class="nav-item parent-item"
							:class="{ active: expandedParents.includes(item.id) }"
							@click="toggleParent(item.id)"
						>
							<span class="nav-icon">{{ item.icon }}</span>
							<span class="nav-label">{{ item.label }}</span>
							<span class="expand-icon" :class="{ rotated: expandedParents.includes(item.id) }">›</span>
						</div>

						<transition name="expand">
							<div v-if="expandedParents.includes(item.id)" class="nav-children">
								<div
									v-for="child in item.children"
									:key="child.id"
									class="nav-item child-item"
									:class="{ active: currentActiveItem === child.id }"
									@click="selectItem(child.id)"
								>
									<span class="nav-icon">{{ child.icon }}</span>
									<span class="nav-label">{{ child.label }}</span>
								</div>
							</div>
						</transition>
					</div>
				</template>
			</div>
		</nav>

		<div v-if="isExpanded" class="nav-overlay" @click="toggleNav"></div>
	</div>
</template>

<script>
export default {
	name: 'AdminSideNav',
	props: {
		activeItem: {
			type: String,
			default: 'dashboard'
		}
	},
	computed: {
		isDarkMode() {
			return this.$parent.isDarkMode;
		},
		currentActiveItem() {
			const currentPath = this.$route.path;

			// Check top-level items
			for (const item of this.menuItems) {
				if (item.route && item.route === currentPath) {
					return item.id;
				}
				// Check children
				if (item.children) {
					for (const child of item.children) {
						if (child.route && child.route === currentPath) {
							return child.id;
						}
					}
				}
			}

			return this.activeItem;
		}
	},
	watch: {
		currentActiveItem(newActiveItem) {
			// Auto-expand parent if active item is a child
			for (const item of this.menuItems) {
				if (item.children) {
					const isChildActive = item.children.some(child => child.id === newActiveItem);
					if (isChildActive && !this.expandedParents.includes(item.id)) {
						this.expandedParents.push(item.id);
					}
				}
			}
		}
	},
	data() {
		return {
			isExpanded: false,
			expandedParents: [],
			menuItems: [
				{
					id: 'dashboard',
					label: 'Dashboard',
					icon: '📊',
					route: '/admin'
				},
				{
					id: 'business',
					label: 'Business',
					icon: '🏢',
					children: [
						{
							id: 'employers',
							label: 'Employers',
							icon: '🏭',
							route: '/admin/employers'
						},
						{
							id: 'customers',
							label: 'Customers',
							icon: '👤',
							route: '/admin/customers'
						},
						{
							id: 'customer-uploads',
							label: 'Customer Uploads',
							icon: '📤',
							route: '/admin/customer-uploads'
						},
						{
							id: 'ai-chat-settings',
							label: 'AI Chat Settings',
							icon: '🤖',
							route: '/admin/ai-chat-settings'
						},
						{
							id: 'ai-chat-messages',
							label: 'AI Chat Messages',
							icon: '💬',
							route: '/admin/ai-chat-messages'
						}
					]
				},
				{
					id: 'content',
					label: 'Content',
					icon: '📝',
					children: [
						// {
						// 	id: 'cms',
						// 	label: 'CMS',
						// 	icon: '📄'
						// },
						{
							id: 'cdn',
							label: 'CDN',
							icon: '🌐',
							route: '/admin/cdn-upload'
						},
						{
							id: 'factoids',
							label: 'Factoids',
							icon: '📚',
							route: '/admin/factoids'
						},
						{
							id: 'guides',
							label: 'Guides',
							icon: '🗺️',
							route: '/admin/guides'
						},
						{
							id: 'quizzes',
							label: 'Quizzes',
							icon: '🧠',
							route: '/admin/quizzes'
						},
						{
							id: 'walkthroughs',
							label: 'Walkthroughs',
							icon: '🎬',
							route: '/admin/walkthroughs'
						},
						{
							id: 'vouchers',
							label: 'Vouchers',
							icon: '🎟️',
							route: '/admin/vouchers'
						},
						{
							id: 'categories',
							label: 'Categories',
							icon: '🏷️',
							route: '/admin/categories'
						},
						{
							id: 'faqs',
							label: 'FAQs',
							icon: '❓',
							route: '/admin/faqs'
						}
					]
				},
				{
					id: 'system',
					label: 'System',
					icon: '⚙️',
					children: [
						{
							id: 'cache',
							label: 'Cache',
							icon: '💾',
							route: '/admin/cache'
						}
					]
				}
			]
		}
	},
	methods: {
		toggleDarkMode() {
			this.$parent.toggleDarkMode();
		},
		toggleNav() {
			this.isExpanded = !this.isExpanded;
		},
		toggleParent(parentId) {
			const index = this.expandedParents.indexOf(parentId);
			if (index > -1) {
				this.expandedParents.splice(index, 1);
			} else {
				this.expandedParents.push(parentId);
			}
		},
		selectItem(itemId) {
			// Find the item in menuItems (including children)
			let item = null;
			for (const menuItem of this.menuItems) {
				if (menuItem.id === itemId) {
					item = menuItem;
					break;
				}
				if (menuItem.children) {
					const child = menuItem.children.find(c => c.id === itemId);
					if (child) {
						item = child;
						break;
					}
				}
			}

			// Navigate to route if item has one, otherwise emit event
			if (item && item.route) {
				this.$router.push(item.route);
			} else {
				this.$emit('item-selected', itemId);
			}

			// Auto-close on mobile after selection
			if (window.innerWidth < 768) {
				this.isExpanded = false;
			}
		}
	}
}
</script>

<style scoped>
.nav-wrapper {
	position: relative;
}

.hamburger-btn {
	display: none;
	background: none;
	border: none;
	cursor: pointer;
	padding: 8px 12px;
	margin-bottom: 15px;
	border-radius: var(--border-radius-s);
	transition: all 0.2s ease;
}

.hamburger-btn:hover {
	background-color: var(--admin-hover-bg);
}

.hamburger-icon {
	display: flex;
	flex-direction: column;
	gap: 5px;
	width: 24px;
	height: 20px;
}

.hamburger-icon span {
	width: 100%;
	height: 2px;
	background-color: var(--admin-text-primary);
	border-radius: 2px;
	transition: all 0.3s ease;
}

.hamburger-icon.active span:nth-child(1) {
	transform: rotate(45deg) translate(8px, 8px);
}

.hamburger-icon.active span:nth-child(2) {
	opacity: 0;
}

.hamburger-icon.active span:nth-child(3) {
	transform: rotate(-45deg) translate(8px, -8px);
}

.admin-side-nav {
	width: 200px;
	background-color: var(--admin-bg-secondary);
	padding: 20px 0;
	margin-right: 20px;
	border-radius: var(--border-radius-m);
	height: fit-content;
	position: sticky;
	top: 10px;
	transition: all 0.3s ease;
	box-shadow: var(--box-shadow);
}

.nav-header {
	padding: 0 20px;
	margin-bottom: 30px;
	display: flex;
	justify-content: space-between;
	align-items: flex-start;
	gap: 10px;
}

.header-title {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 8px;
	flex: 1;
	padding-bottom: 15px;
	border-bottom: 1px solid var(--admin-border);
}

.nav-header h2 {
	color: var(--admin-text-primary);
	font-size: 18px;
	margin: 0;
	transition: color 0.3s ease;
}

.dark-mode-toggle {
	display: flex;
	align-items: center;
	justify-content: center;
	background: none;
	border: none;
	font-size: 18px;
	cursor: pointer;
	padding: 4px 8px;
	border-radius: var(--border-radius-s);
	transition: all 0.2s ease;
	color: var(--admin-text-primary);
}

.dark-mode-toggle:hover {
	background-color: var(--admin-hover-bg);
	transform: scale(1.1);
}

.close-btn {
	display: none;
	background: none;
	border: none;
	color: var(--admin-text-primary);
	font-size: 24px;
	cursor: pointer;
	padding: 0;
	margin-left: 10px;
	transition: all 0.2s ease;
}

.close-btn:hover {
	opacity: 0.7;
}

.nav-menu {
	display: flex;
	flex-direction: column;
	gap: 5px;
}

.nav-item {
	padding: 12px 20px;
	color: var(--admin-text-secondary);
	cursor: pointer;
	display: flex;
	align-items: center;
	gap: 12px;
	transition: all 0.2s ease;
	font-size: 14px;
	user-select: none;
}

.nav-item:hover {
	color: var(--admin-text-primary);
	background-color: var(--admin-hover-bg);
}

.nav-item.active {
	color: var(--admin-text-primary);
	background-color: var(--admin-active-bg);
	border-left: 3px solid var(--background-color-alt6);
	padding-left: 17px;
}

:root.light-mode .nav-item.active {
	background-color: #f0f0f0 !important;
	color: var(--admin-text-primary) !important;
	border-left-color: var(--background-color-alt6) !important;
}

:root.light-mode .child-item.active {
	background-color: #f0f0f0 !important;
	color: var(--admin-text-primary) !important;
	border-left-color: var(--background-color-alt6) !important;
}

.nav-icon {
	font-size: 18px;
	min-width: 24px;
}

.nav-label {
	font-family: gotham-heavy;
}

.nav-parent {
	display: flex;
	flex-direction: column;
}

.parent-item {
	position: relative;
	padding-right: 30px;
}

.expand-icon {
	position: absolute;
	right: 15px;
	font-size: 20px;
	transition: transform 0.3s ease;
	font-family: gotham-heavy;
}

.expand-icon.rotated {
	transform: rotate(90deg);
}

.nav-children {
	display: flex;
	flex-direction: column;
	gap: 0;
	background-color: var(--admin-hover-bg);
}

.child-item {
	padding-left: 40px;
	font-size: 13px;
	color: var(--admin-text-secondary);
	background-color: transparent;
}

.child-item:hover {
	color: var(--admin-text-primary);
	background-color: var(--admin-hover-bg);
}

.child-item.active {
	color: var(--admin-text-primary);
	background-color: var(--admin-active-bg);
	border-left: 3px solid var(--background-color-alt6);
	padding-left: 37px;
}

.expand-enter-active,
.expand-leave-active {
	transition: all 0.3s ease;
}

.expand-enter-from,
.expand-leave-to {
	opacity: 0;
	max-height: 0;
}

.expand-enter-to,
.expand-leave-from {
	opacity: 1;
	max-height: 500px;
}

.nav-overlay {
	display: none;
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background-color: rgba(0, 0, 0, 0.5);
	z-index: 998;
	animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
	from {
		opacity: 0;
	}
	to {
		opacity: 1;
	}
}

@media (max-width: 768px) {
	.hamburger-btn {
		display: flex;
		align-items: center;
		justify-content: center;
	}

	.admin-side-nav {
		width: 250px;
		margin-right: 0;
		position: fixed;
		left: -250px;
		top: 0;
		height: 100vh;
		z-index: 999;
		border-radius: 0;
		overflow-y: auto;
		transition: left 0.3s ease;
		padding: 20px 0;
		top: 60px;
		height: calc(100vh - 60px);
	}

	.admin-side-nav.expanded {
		left: 0;
	}

	.nav-header {
		padding: 0 20px;
		margin-bottom: 30px;
		padding-top: 10px;
	}

	.nav-header h2 {
		padding-bottom: 15px;
	}

	.close-btn {
		display: block;
	}

	.nav-overlay {
		display: block;
	}

	.nav-overlay {
		animation: fadeIn 0.3s ease;
	}

	.nav-menu {
		flex-direction: column;
	}

	.nav-item {
		padding: 12px 20px;
		flex-direction: row;
	}

	.nav-icon {
		font-size: 18px;
	}

	.nav-label {
		font-size: 14px;
		text-align: left;
	}

	.nav-item.active {
		border-left: 3px solid #ffd700;
		padding-left: 17px;
	}
}

@media (max-width: 480px) {
	.admin-side-nav {
		width: 200px;
		left: -200px;
	}

	.admin-side-nav.expanded {
		left: 0;
	}
}
</style>
